import { Injectable } from '@angular/core';

// Reference global window.host API (defined in window-host.d.ts)
declare const window: Window & typeof globalThis;

/**
 * Module definition for reusable test sequences (login, navigation, etc.)
 */
export interface Module {
  id?: string;
  moduleType: 'login' | 'navigate' | 'switch_tenant' | 'logout' | 'custom';
  name: string;
  description?: string;
  steps: ModuleStep[];
  isActive?: boolean;
}

/**
 * Module step - similar to TestStep but used in modules
 */
export interface ModuleStep {
  action: string;
  selector?: any;
  value?: string;
  text?: string;
  timeout?: number;
  description?: string;
}

/**
 * Result of module execution
 */
export interface ModuleExecutionResult {
  success: boolean;
  moduleName: string;
  executedSteps: number;
  duration: number;
  error?: string;
  failedStepIndex?: number;
}

/**
 * Service for executing reusable modules (login, navigation, etc.) during precondition setup
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleExecutorService {
  
  constructor() {}

  /**
   * Execute a module with placeholder resolution
   * @param module Module to execute
   * @param sessionHandle Window handle for session execution
   * @param params Parameters for placeholder resolution (e.g., username, password)
   * @returns Module execution result
   */
  async executeModule(
    module: Module, 
    sessionHandle: number, 
    params: Record<string, any>
  ): Promise<ModuleExecutionResult> {
    const startTime = Date.now();
    
    try {
      for (let i = 0; i < module.steps.length; i++) {
        const step = module.steps[i];
                
        // Resolve placeholders in step
        const resolvedStep = this.resolveStepPlaceholders(step, params);
      
        // Execute step via session window
        const host = (window as any).host;
        if (!host?.executeStepInSession) {
          throw new Error('Session execution API not available');
        }
                
        // Add timeout to prevent hanging
        const stepTimeout = (resolvedStep.timeout || 30) * 1000;
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error(`Step ${i + 1} execution timeout after ${stepTimeout}ms`)), stepTimeout)
        );
        const executePromise = host.executeStepInSession(sessionHandle, resolvedStep);
        
        const result = await Promise.race([executePromise, timeoutPromise]);
                
        if (!result.success) {
          return {
            success: false,
            moduleName: module.name,
            executedSteps: i + 1,
            duration: Date.now() - startTime,
            error: result.error || 'Step execution failed',
            failedStepIndex: i
          };
        }
        
        // Small delay between steps for stability
        await this.sleep(300);
      }
      
      const duration = Date.now() - startTime;
      
      return {
        success: true,
        moduleName: module.name,
        executedSteps: module.steps.length,
        duration
      };
      
    } catch (error: any) {
      return {
        success: false,
        moduleName: module.name,
        executedSteps: 0,
        duration: Date.now() - startTime,
        error: error?.message || String(error)
      };
    }
  }

  /**
   * Generate a basic login module from auth profile credentials
   * This is a template-based approach - can be customized per application
   * @param authProfile Auth profile with username and password
   * @param loginSelectors Optional custom selectors for login form
   * @returns Login module
   */
  generateLoginModule(
    authProfile: { username?: string; password?: string; profileName?: string },
    loginSelectors?: {
      usernameSelector?: string;
      passwordSelector?: string;
      submitSelector?: string;
    }
  ): Module {
    // Default selectors - adjust based on your application
    const defaultSelectors = {
      usernameSelector: 'input[type="text"][name="username"]',
      passwordSelector: 'input[type="password"][name="password"]',
      submitSelector: 'button[type="submit"]'
    };
    
    const selectors = { ...defaultSelectors, ...loginSelectors };
    
    return {
      moduleType: 'login',
      name: `Login as ${authProfile.profileName || authProfile.username}`,
      description: `Automated login using auth profile credentials`,
      steps: [
        {
          action: 'fill',
          selector: selectors.usernameSelector,
          value: '{{username}}',
          description: 'Enter username',
          timeout: 5000
        },
        {
          action: 'fill',
          selector: selectors.passwordSelector,
          value: '{{password}}',
          description: 'Enter password',
          timeout: 5000
        },
        {
          action: 'click',
          selector: selectors.submitSelector,
          description: 'Click login button',
          timeout: 5000
        },
        {
          action: 'wait',
          value: '2000',
          description: 'Wait for login to complete'
        }
      ],
      isActive: true
    };
  }

  /**
   * Generate a navigation module from fallback steps
   * @param targetScreen Target screen information
   * @param fallbackSteps Steps to navigate to the screen
   * @returns Navigation module
   */
  generateNavigationModule(
    targetScreen: { screenId: string; displayName?: string },
    fallbackSteps: any[]
  ): Module {
    return {
      moduleType: 'navigate',
      name: `Navigate to ${targetScreen.displayName || targetScreen.screenId}`,
      description: `Navigate to target screen using fallback steps`,
      steps: fallbackSteps.map((step: any) => ({
        action: step.action,
        selector: step.selector,
        value: step.value,
        text: step.text,
        timeout: step.timeout || 5000,
        description: step.description
      })),
      isActive: true
    };
  }

  /**
   * Resolve placeholders in a step
   * Replaces {{key}} with values from params
   * @param step Step with potential placeholders
   * @param params Parameter values
   * @returns Step with resolved placeholders
   */
  private resolveStepPlaceholders(step: ModuleStep, params: Record<string, any>): ModuleStep {
    // Serialize step to JSON, replace placeholders, deserialize
    let stepJson = JSON.stringify(step);
    
    for (const [key, value] of Object.entries(params)) {
      const placeholder = `{{${key}}}`;
      const replacement = String(value ?? '');
      stepJson = stepJson.split(placeholder).join(replacement);
    }
    
    return JSON.parse(stepJson);
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
